/**
 * Workspace — the card's reconciler, the one writer of `workspaceCard`
 * (@momo/views), keyed on the open organization.
 *
 * Two ways the listener can leave the card at `loading` forever, and each
 * needs its own answer. An ERROR is answered by retry, not catchError, whose
 * contract is stream conclusion and would end the listener on the first
 * fault. SILENCE has no error to catch — an offline snapshot listener invokes
 * neither next nor error — so a bounded timer raced against the source
 * settles the cell, while the live subscription stays open and a real
 * snapshot supersedes it whenever one arrives.
 */
import type { Workspace } from "@momo/types";
import { organizationId } from "@momo/views/organization";
import { workspaceCard } from "@momo/views/workspace";
import {
	map,
	merge,
	type Observable,
	of,
	retry,
	type Subscription,
	share,
	startWith,
	switchMap,
	takeUntil,
	tap,
	timer,
} from "rxjs";

export function mountWorkspaceCard(
	card$: (orgId: string) => Observable<Workspace.Workspace | null>,
): Subscription {
	return organizationId.$.pipe(
		switchMap((orgId) => {
			// No organization open: nothing will ever answer, so the cell settles
			// here rather than reading as a card still on its way.
			if (orgId === null)
				return of({ status: "none", workspace: null } as const);
			const live$ = card$(orgId).pipe(
				retry({
					delay: (err: unknown) => {
						console.warn("workspace card listener failed; retrying", err);
						return timer(2000);
					},
				}),
				share(),
			);
			return merge(
				live$.pipe(
					map((workspace) =>
						workspace === null
							? ({ status: "not-found", workspace: null, orgId } as const)
							: ({ status: "ready", workspace, orgId } as const),
					),
				),
				timer(8000).pipe(
					takeUntil(live$),
					map(() => ({ status: "not-found", workspace: null, orgId }) as const),
				),
			).pipe(startWith({ status: "loading", workspace: null } as const));
		}),
		tap((next) => workspaceCard.replace(next)),
	).subscribe();
}
